/**
 * AiConsentSheet — the ONE explicit AI-processing consent gate shown before
 * the first AI action in each flow (App Store Guideline 5.1.2(i) / GDPR
 * Art. 6(1)(a)).
 *
 * Built on the shared `AppModal` primitive (centered card — NOT a bottom
 * sheet, per the modal invariants) and modelled directly on
 * `VoiceConsentSheet`: plain, specific copy about what is sent, to whom, that
 * it is optional, and how to withdraw — no dark patterns, equal visual
 * weight for "Not now" and "I agree".
 *
 * STATELESS ON PURPOSE, same as `VoiceConsentSheet`. `AppModal` never
 * unmounts its children, so any local state here would need an explicit
 * reset-on-close wrapper — this component sidesteps that whole class of bug
 * by not holding any: `visible`, `isAccepting` and the pending action all
 * live in `useAiConsentGate` (`@/lib/consent/ai-consent.ts`), which already
 * resets its pending-action ref on every close.
 *
 * Only the REQUESTED types are listed (R-D-SHEET) — a screen that only needs
 * `ai_scoring` never mentions pitch-deck analysis.
 */

import React from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { Sparkles, ShieldCheck, Search, Settings2, AlertCircle } from 'lucide-react-native';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import { AI_CONSENT_PURPOSES } from '@/lib/consent/ai-consent';
import type { AiConsentType } from '@/lib/consent/ai-consent';

/** Verified against `.planning/compliance/vendor-processor-register.md` rows
 *  36-49 (2026-09-16): Mistral AI is the primary LLM/embedding/STT processor,
 *  EU-hosted (Paris); Anthropic and OpenAI are the configured fallbacks, both
 *  US-based. */
const PROVIDER_LINE =
  'Mistral AI (EU) processes it; if Mistral is unavailable, Anthropic or OpenAI may process it instead, under data-processing agreements that forbid training on your data.';

const SEARCH_PROVIDER_LINE =
  'This analysis also sends derived startup context to external search providers (Tavily, Perplexity) to research your market, industry, and competitors.';

interface ConsentPointProps {
  icon: React.ReactNode;
  text: string;
  colors: ReturnType<typeof useThemeColors>;
}

function ConsentPoint({ icon, text, colors }: ConsentPointProps) {
  return (
    <View className="flex-row items-start mb-4" style={{ gap: 12 }}>
      <View
        className="w-9 h-9 rounded-xl items-center justify-center"
        style={{ backgroundColor: colors.primary[500] + '1A' }}
      >
        {icon}
      </View>
      <Text
        className="flex-1 text-[14px] leading-5"
        style={{ color: colors.text.secondary, paddingTop: 2 }}
      >
        {text}
      </Text>
    </View>
  );
}

export interface AiConsentSheetProps {
  visible: boolean;
  /** Only the requested types are described — never the full catalogue. */
  types: AiConsentType[];
  /** Disables both actions and shows a spinner on "I agree" while the write is in flight. */
  isAccepting?: boolean;
  /**
   * A `ConsentVerificationError` message from the last accept attempt — a
   * recovery (403) accept whose fresh read found nothing missing, which
   * does not explain the refusal. Shown inline rather than silently retried,
   * which is what would otherwise loop: tap "I agree" -> 403 -> tap again.
   */
  errorMessage?: string | null;
  onAccept: () => void;
  onDecline: () => void;
  /**
   * Tapping "Read our full AI disclosure". NOT a plain navigation: this modal
   * is a native RN `Modal`, and navigating while it is still open pushes the
   * disclosure page in UNDERNEATH it — the modal stays on top and nothing
   * appears to happen. The caller must dismiss the sheet first, then
   * navigate once it is actually gone (`useAiConsentGate`'s
   * `handleOpenDisclosure`: decline, then `router.push`).
   */
  onOpenDisclosure: () => void;
}

export function AiConsentSheet({ visible, types, isAccepting, errorMessage, onAccept, onDecline, onOpenDisclosure }: AiConsentSheetProps) {
  const colors = useThemeColors();
  const includesStrategicAnalysis = types.includes('strategic_analysis');

  return (
    <AppModal
      visible={visible}
      onClose={onDecline}
      title="Allow AI processing?"
      footer={
        <AppModal.Actions>
          <ModalAction label="Not now" variant="neutral" onPress={onDecline} disabled={isAccepting} />
          <ModalAction
            label="I agree"
            variant="primary"
            disabled={isAccepting}
            icon={
              isAccepting ? (
                <ActivityIndicator size="small" color={colors.text.inverse} />
              ) : (
                <Sparkles size={16} color={colors.text.inverse} />
              )
            }
            onPress={onAccept}
          />
        </AppModal.Actions>
      }
    >
      <Text className="text-[15px] leading-6 mb-5" style={{ color: colors.text.primary }}>
        This uses AI to analyze the documents and profile information you provide. Here is exactly
        what happens:
      </Text>

      {errorMessage && (
        <View
          className="flex-row items-start gap-2 rounded-xl px-3 py-3 mb-4"
          style={{ backgroundColor: colors.red.light }}
        >
          <AlertCircle size={16} color={colors.error} style={{ marginTop: 1 }} />
          <Text className="flex-1 text-[13px] leading-5" style={{ color: colors.error }}>
            {errorMessage}
          </Text>
        </View>
      )}

      {/* Verbatim server purpose text, one point per requested type — the
          audit-trail `purpose` column reads the same sentence. */}
      {types.map((type) => (
        <ConsentPoint
          key={type}
          colors={colors}
          icon={<Sparkles size={17} color={colors.primary[600]} />}
          text={AI_CONSENT_PURPOSES[type]}
        />
      ))}

      <ConsentPoint
        colors={colors}
        icon={<ShieldCheck size={17} color={colors.primary[600]} />}
        text={PROVIDER_LINE}
      />

      {includesStrategicAnalysis && (
        <ConsentPoint
          colors={colors}
          icon={<Search size={17} color={colors.primary[600]} />}
          text={SEARCH_PROVIDER_LINE}
        />
      )}

      <ConsentPoint
        colors={colors}
        icon={<Settings2 size={17} color={colors.primary[600]} />}
        text="This is optional. You can withdraw consent at any time in Settings → Privacy & Security."
      />

      <Text
        onPress={onOpenDisclosure}
        accessibilityRole="link"
        className="text-[14px] font-medium mt-1"
        style={{ color: colors.primary[600] }}
      >
        Read our full AI disclosure
      </Text>
    </AppModal>
  );
}
